import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { resolveEditorTab } from "@ckgo2/shared";
import { ApiError } from "@/api/client";
import { getFirstTabWithErrors } from "@/lib/fieldTabMap";

/**
 * Aktivní záložka editoru v adrese (`?tab=<id>`, vize F9.2, řez 09 Kontrakt bod 5): přežije
 * obnovení stránky, uložení i přechod nové položky z `/new` na `/<id>` (`useEditPage` předá
 * `location.search`).
 *
 * Záložka se při každém renderu jen ODVOZUJE (`resolveEditorTab`): chybějící, neznámé nebo
 * skryté id = první viditelná záložka. Hook do adresy sám nikdy nepíše — žádný efekt, který
 * by neplatné `tab` „srovnal": viditelnost záložky Hotel závisí na `tripType` z formuláře,
 * který dojede až po načtení detailu, a normalizace by `?tab=hotel` přepsala na první záložku
 * dřív, než formulář dojede. Píše jen `setTab` (klik, přepnutí na chybu, větev souřadnic).
 *
 * Zápis s `replace: true`: přepnutí záložky nepřidává záznam do historie (šipka prohlížeče
 * vede tam, odkud redaktor přišel, precedent `StudioPage`), ostatní parametry adresy zůstanou.
 */
export function useEditorTab(
  visibleTabIds: readonly string[],
): readonly [string, (id: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveEditorTab(searchParams.get("tab"), visibleTabIds);

  // `setSearchParams` z React Routeru mění identitu s každou změnou adresy (závisí na
  // `searchParams`). `setTab` je proto stabilní: čte poslední `setSearchParams` z refu, takže
  // ho volající smí dát do deps efektu nebo `useCallback`, aniž by se efekt spouštěl znovu po
  // každém kliknutí na záložku.
  const setSearchParamsRef = useRef(setSearchParams);
  useLayoutEffect(() => {
    setSearchParamsRef.current = setSearchParams;
  }, [setSearchParams]);

  const setTab = useCallback((id: string) => {
    setSearchParamsRef.current(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", id);
        return next;
      },
      { replace: true },
    );
  }, []);

  return [activeTab, setTab] as const;
}

/**
 * Po neúspěšném uložení přepne editor na záložku s první chybou pole (vize F9.2). Záložka se
 * počítá z `ApiError.fieldErrors` (klíče serverové chyby), ne z `form.formState.errors`: proxy
 * `formState` odebírá jen klíče přečtené při renderu, takže efekt v editoru, který `errors`
 * nečte, by viděl zastaralý stav. Chyba bez polí (409 konflikt, horní chyba rodiče, síť)
 * záložku nemění. Identita chyby mutace se mění při každém selhání, takže opakované selhání
 * přepne znovu. Pole bez záznamu v `mapping` záložku nepřepne (zůstane `ErrorBanner`).
 */
export function useSwitchTabOnSaveError(
  saveError: unknown,
  mapping: Record<string, string>,
  setTab: (id: string) => void,
): void {
  useEffect(() => {
    if (!(saveError instanceof ApiError) || !saveError.fieldErrors) return;
    const tab = getFirstTabWithErrors(saveError.fieldErrors, mapping);
    if (tab) setTab(tab);
    // `setTab` z `useEditorTab` je stabilní a `mapping` je u volajících konstanta modulu, takže
    // efekt běží jen při nové chybě, ne po kliknutí na jinou záložku.
  }, [saveError, mapping, setTab]);
}
